import React, { useState, useEffect } from 'react';
import { alertService } from '../../services/alertService';
import { TrendingUp, TrendingDown, Percent, Check } from 'lucide-react';
import { toast } from 'sonner';
import Button from '../shared/Button';

interface Props {
  onSuccess: () => Promise<void>;
}

type AlertType = 'price_above' | 'price_below' | 'change_pct_above';

const DELIVERY_CHANNELS = [
  { id: 'telegram', label: 'Telegram' },
  { id: 'inApp', label: 'In-app' }
] as const;

const inputClass =
  'w-full bg-gray-50 dark:bg-[#141e30] border border-gray-200 dark:border-cat-border px-4 py-3 rounded-xl text-gray-900 dark:text-cat-text-main text-sm placeholder-gray-400 dark:placeholder-cat-text-dark outline-none transition-colors focus:border-cat-accent';

export const CreateAlertForm: React.FC<Props & { initialTicker?: string; fixedTicker?: boolean }>
 = ({ onSuccess, initialTicker = '', fixedTicker = false }) => {
  const [ticker, setTicker] = useState(initialTicker || '');
  const [alertType, setAlertType] = useState<AlertType | ''>('');
  const [targetValue, setTargetValue] = useState('');
  const [delivery, setDelivery] = useState({ telegram: true, inApp: true });
  const [loading, setLoading] = useState(false);
  const [uiError, setUiError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    setTicker(initialTicker || '');
  }, [initialTicker]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUiError(null);
    setSuccessMsg(null);

    if (!ticker) {
      setUiError("Please enter a valid ticker");
      return;
    }
    if (!alertType) {
      setUiError("Please select an alert type (Price Up/Down or % Change)");
      return;
    }
    const val = parseFloat(targetValue);
    if (isNaN(val) || val <= 0) {
      setUiError("Please enter a valid target value greater than 0");
      return;
    }
    if (!delivery.telegram && !delivery.inApp) {
      setUiError("Please select at least one delivery channel");
      return;
    }

    setLoading(true);
    try {
      const selectedChannels: Array<'telegram' | 'in_app'> = [];
      if (delivery.telegram) selectedChannels.push('telegram');
      if (delivery.inApp) selectedChannels.push('in_app');

      const createdTicker = ticker;

      await alertService.createAlert({
        ticker,
        alert_type: alertType,
        target_value: val,
        channels: selectedChannels,
      } as any);

      if (!fixedTicker) {
        setTicker('');
      }
      setAlertType('');
      setTargetValue('');

      const msg = `Alert created for ${createdTicker}`;
      setSuccessMsg(msg);
      toast.success(msg, { description: 'Your alert is now active and monitoring.' });
      setTimeout(() => setSuccessMsg(null), 4000);

      await onSuccess();
    } catch (err: any) {
      console.error("Error creating alert:", err);
      const status = err.response?.status || err.status;
      const backendMessage = err.response?.data?.message;

      if (status === 400) {
        setUiError(backendMessage || "Ticker does not exist in the stock market.");
      } else if (status === 409) {
        setUiError("This alert already exists in your system.");
      } else if (status === 503) {
        setUiError("Market data service is temporarily unavailable. Please try again later.");
      } else {
        setUiError("Internal server error: The server could not process the request. Check backend logs.");
      }
   } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full rounded-2xl border border-gray-200 dark:border-cat-border bg-white dark:bg-cat-surface shadow-sm overflow-hidden"
    >
      <div className="h-[3px] bg-gradient-to-r from-[#a78bfa] via-[#818cf8] to-cat-accent-2" />
      <div className="p-5">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.15em] text-gray-500 dark:text-cat-text-muted mb-5">
          Create Alert
        </h3>

        <input
          className={`${inputClass} mb-4 ${fixedTicker ? 'opacity-60 cursor-not-allowed bg-gray-100 dark:bg-cat-elevated' : ''}`}
          placeholder="Ticker (e.g. NVDA)"
          value={ticker}
          readOnly={fixedTicker}
          onChange={e => {
            if (fixedTicker) return;
            const inputValue = e.target.value;

            if (/[^A-Za-z0-9.-]/.test(inputValue) && inputValue !== '') {
              setUiError("A stock name can only contain English letters, numbers, a period, or a hyphen");
            } else {
              setUiError(null);
            }

            setTicker(inputValue.replace(/[^A-Za-z0-9.-]/g, '').toUpperCase());
          }}
        />

        <div className="grid grid-cols-3 gap-2 mb-4">
          {(['price_above', 'price_below', 'change_pct_above'] as AlertType[]).map((type) => (
            <button
              type="button"
              key={type}
              onClick={(e) => {
                e.preventDefault();
                setAlertType(type);
              }}
              className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl border transition ${
                alertType === type
                  ? 'bg-[#4f8ff8]/10 border-cat-accent text-cat-accent'
                  : 'bg-gray-50 dark:bg-[#141e30] border-gray-200 dark:border-cat-border text-gray-400 dark:text-cat-text-muted hover:border-gray-300 dark:hover:border-cat-border-2'
              }`}
            >
              {type === 'price_above' ? <TrendingUp size={14}/> : type === 'price_below' ? <TrendingDown size={14}/> : <Percent size={14}/>}
              <span className="text-[9px] font-bold uppercase">{type === 'price_above' ? 'Price ↑' : type === 'price_below' ? 'Price ↓' : '% Change'}</span>
            </button>
          ))}
        </div>

        <input
          type="number"
          min="0.01"
          step="any"
          className={`${inputClass} mb-5`}
          placeholder="Target Value"
          value={targetValue}
          onChange={e => setTargetValue(e.target.value)}
        />

        <div className="space-y-3 mb-5">
          {DELIVERY_CHANNELS.map((item) => (
            <label key={item.id} className="flex items-center gap-3 cursor-pointer select-none">
              <div className={`w-5 h-5 rounded-md flex items-center justify-center border transition ${delivery[item.id] ? 'bg-gradient-to-r from-cat-accent to-cat-accent-2 border-transparent' : 'bg-gray-50 dark:bg-[#141e30] border-gray-200 dark:border-cat-border'}`}>
                {delivery[item.id] && <Check size={14} className="text-white" />}
              </div>
              <input
                type="checkbox"
                className="hidden"
                checked={delivery[item.id]}
                onChange={() => setDelivery(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
              />
              <span className="text-xs text-gray-500 dark:text-cat-text-muted">{item.label}</span>
            </label>
          ))}
        </div>

        {uiError && (
          <div className="text-cat-red text-[11px] font-semibold mt-2 mb-3 text-center bg-[#f5476a]/10 p-2.5 rounded-xl border border-[#f5476a]/20">
            {uiError}
          </div>
        )}

        {successMsg && (
          <div className="text-emerald-700 dark:text-cat-green text-[11px] font-semibold mt-2 mb-3 text-center bg-emerald-50 dark:bg-[#10d98e]/10 p-2.5 rounded-xl border border-emerald-200 dark:border-[#10d98e]/20 flex items-center justify-center gap-1.5">
            <Check size={14} />
            {successMsg}
          </div>
        )}

        <Button type="submit" loading={loading} className="w-full" size="md">
          {loading ? 'Creating...' : 'Create Alert'}
        </Button>
      </div>
    </form>
  );
};
